import { BayLcgVcgTcgAndPairings, I20LcgsByBay } from "@tedivo/tedivo-bay-grid-pure";
import {
  IValidationInput,
  IValidationOutput,
  IValidator,
  TValidatorSeverity,
  TValidatorSpecialScopes,
  TValidatorStage,
} from "./types";
import { createDictionary, createDictionaryMultiple } from "@tedivo/tedivo-pure-helpers";

import { IOpenVesselDefinitionV1 } from "open-vessel-definition";
import { allBaysWith40sArePairedValidator } from "./baysData/allBaysWith40sArePairedValidator";
import { bay40sLocationValidator } from "./baysData/bay40sLocationValidator";
import { bayLevelStructureValidator } from "./structure/bayLevelStructureValidator";
import { centerLineRowValidator } from "./baysData/centerLineRowValidator";
import { cgsValidator } from "./CGs/cgsValidator";
import { lidDataForBaysValidator } from "./lids/lidDataForBaysValidator";
import { lidStructureValidator } from "./structure/lidStructureValidator";
import { rootStructureValidator } from "./structure/rootStructureValidator";
import { rowWeightsValidator } from "./baysData/rowWeightsValidator";
import { slotStructureValidator } from "./structure/slotStructureValidator";
import { vesselPartsStructureValidator } from "./structure/vesselPartsStructureValidator";

export const VALIDATOR_STAGES: readonly TValidatorStage[] = ["structure", "data", "semantic"];

export interface IValidateOptions {
  /** Only run these stages. Defaults to all, in order */
  stages?: TValidatorStage[];
}

/** The translation keys of the outputs the engine itself produces */
export const SKIPPED_STRUCTURE_KEY = "validators:common.skippedStructure";
export const CRASHED_KEY = "validators:common.crashed";

export function stageOf(validator: IValidator<unknown>): TValidatorStage {
  return validator.stage ?? "semantic";
}

export class OvdValidator {
  /** Registration order is kept within each stage. Stages run structure → data → semantic */
  static validators: IValidator<unknown>[] = [
    rootStructureValidator,
    bayLevelStructureValidator,
    slotStructureValidator,
    lidStructureValidator,
    vesselPartsStructureValidator,
    allBaysWith40sArePairedValidator,
    rowWeightsValidator,
    bay40sLocationValidator,
    centerLineRowValidator,
    lidDataForBaysValidator,
    cgsValidator,
  ];

  private lastResult: IValidationOutput<unknown>[] = [];
  private lastResultByName: { [key: string]: IValidationOutput<unknown> } | undefined = undefined;

  constructor() {
    const valsById = createDictionaryMultiple(OvdValidator.validators, (a) => a.name);

    /** Check there aren't duplicate validators by name */
    Object.keys(valsById).forEach((key) => {
      const vals = valsById[key];
      if (vals.length > 1) {
        throw new Error(`Duplicate validator name: ${key}`);
      }
    });
  }

  /**
   * Runs the structure validators only, on the raw JSON. Use it to decide whether the
   * file can be handed to the CG pipeline (`BayLcgVcgTcgAndPairings`) at all.
   */
  static validateStructure(json: IOpenVesselDefinitionV1): IValidationOutput<unknown>[] {
    return OvdValidator.run(json, undefined, undefined, ["structure"]);
  }

  /** Execute all the validators and cache the result */
  validate(
    json: IOpenVesselDefinitionV1,
    lcgVcgTcgAndPairings: BayLcgVcgTcgAndPairings | undefined,
    lcgsBy20Bay: I20LcgsByBay | undefined,
    options?: IValidateOptions,
  ): IValidationOutput<unknown>[] {
    const result = OvdValidator.run(
      json,
      lcgVcgTcgAndPairings,
      lcgsBy20Bay,
      options?.stages ?? VALIDATOR_STAGES,
    );

    this.lastResult = result;
    this.lastResultByName = undefined;

    return result;
  }

  private static run(
    json: IOpenVesselDefinitionV1,
    lcgVcgTcgAndPairings: BayLcgVcgTcgAndPairings | undefined,
    lcgsBy20Bay: I20LcgsByBay | undefined,
    stages: readonly TValidatorStage[],
  ): IValidationOutput<unknown>[] {
    const input: IValidationInput = { data: { json, lcgVcgTcgAndPairings, lcgsBy20Bay } };
    const results: IValidationOutput<unknown>[] = [];
    let structureOk = true;

    VALIDATOR_STAGES.forEach((stage) => {
      if (!stages.includes(stage)) return;

      OvdValidator.validators
        .filter((validator) => stageOf(validator) === stage)
        .forEach((validator) => {
          if (stage !== "structure" && !structureOk) {
            results.push(notRun(validator, SKIPPED_STRUCTURE_KEY, "info"));
            return;
          }

          results.push(runSafely(validator, input));
        });

      if (stage === "structure") {
        structureOk = !results.some((r) => r.stage === "structure" && r.severity === "error");
      }
    });

    return results;
  }

  /** Get the last result of the validators by scope */
  public getLastResult = (scopes?: TValidatorSpecialScopes[]): IValidationOutput<unknown>[] => {
    return this.lastResult.filter(
      (a) => !scopes || (a.specialScopes && a.specialScopes.some((sc) => scopes.includes(sc))),
    );
  };

  /** The last results of a stage */
  public getLastResultByStage = (stage: TValidatorStage): IValidationOutput<unknown>[] =>
    this.lastResult.filter((a) => a.stage === stage);

  /** The last results that failed with exactly this severity */
  public getLastResultBySeverity = (severity: TValidatorSeverity): IValidationOutput<unknown>[] =>
    this.lastResult.filter((a) => a.severity === severity);

  /** Gets a single result by name */
  public getLastResultByName = <T>(name: string) => {
    if (!this.lastResultByName) {
      this.lastResultByName = createDictionary(this.lastResult, (a) => a.name);
    }

    return this.lastResultByName[name] as IValidationOutput<T> | undefined;
  };
}

function runSafely(
  validator: IValidator<unknown>,
  input: IValidationInput,
): IValidationOutput<unknown> {
  try {
    const out = validator.fn(input);
    // Validators written before stages existed don't tag their output
    if (out.stage === undefined) out.stage = stageOf(validator);
    if (out.severity === undefined && !out.isValid) out.severity = validator.severity ?? "error";
    return out;
  } catch {
    return notRun(validator, CRASHED_KEY, "error");
  }
}

function notRun(
  validator: IValidator<unknown>,
  messageTranslationKey: string,
  severity: TValidatorSeverity,
): IValidationOutput<unknown> {
  return {
    name: validator.name,
    isValid: false,
    messageTranslationKey,
    specialScopes: validator.specialScopes,
    severity,
    stage: stageOf(validator),
    skipped: true,
    result: undefined,
  };
}
